import { Select, SelectItem } from '@tremor/react';
import { t } from '../lib/i18n';
import type { DashboardWidget, Store } from '../api/types';

interface Props {
  widget: DashboardWidget;
  editing: boolean;
  isFirst: boolean;
  isLast: boolean;
  /** Chart widgets only — populates the store-filter select. */
  stores: Store[];
  onMove: (delta: number) => void;
  onRemove: () => void;
  onChange: (next: DashboardWidget) => void;
  children: React.ReactNode;
}

/**
 * Edit-mode chrome around one dashboard widget (JCD-17 phase 3). View mode
 * (editing=false) renders `children` with no wrapper — byte-identical to
 * phase 2. Edit mode overlays a toolbar: reorder (‹ ›, the same swap-with-
 * neighbor nudge ColumnPicker.tsx already uses — real drag was descoped
 * there too), remove (✕), column-span toggle (1|2), and the one filter that's
 * actually wireable per kind: a store select for charts (client-side slice
 * of the shared /api/overview snapshot — see lib/overviewStoreSlice.ts) or a
 * date range for reports (server-side override — see api/reports.ts
 * ReportRunOverrides). Filters live on `widget.filters`, applied by the
 * caller (Tablero.tsx) when it renders `children` — this component only
 * edits the widget object, it never fetches or renders data itself.
 */
export default function WidgetFrame({ widget, editing, isFirst, isLast, stores, onMove, onRemove, onChange, children }: Props) {
  if (!editing) return <>{children}</>;

  const et = t.tablero.edit;
  const filters = widget.filters ?? {};
  const sid = typeof filters.sid === 'number' ? filters.sid : null;

  return (
    <div className="rounded-tremor-default border-2 border-dashed border-tremor-brand-emphasis/40 p-2 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="flex items-center gap-0.5">
          <button
            type="button"
            className="hover:text-tremor-content-strong disabled:opacity-30"
            onClick={() => onMove(-1)}
            disabled={isFirst}
            aria-label={et.moveLeft}
          >‹</button>
          <button
            type="button"
            className="hover:text-tremor-content-strong disabled:opacity-30"
            onClick={() => onMove(1)}
            disabled={isLast}
            aria-label={et.moveRight}
          >›</button>
        </span>

        <span className="flex items-center gap-1">
          <span className="text-tremor-content">{et.span}</span>
          {[1, 2].map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => onChange({ ...widget, w })}
              className={
                'px-1.5 py-0.5 rounded-tremor-small border ' +
                ((widget.w ?? 1) === w
                  ? 'border-tremor-brand-emphasis bg-tremor-brand-faint text-tremor-brand-emphasis'
                  : 'border-tremor-border text-tremor-content')
              }
            >{w}</button>
          ))}
        </span>

        {widget.kind === 'chart' && stores.length > 1 && (
          <span className="flex items-center gap-1">
            <span className="text-tremor-content">{et.storeFilter}</span>
            <Select
              value={sid === null ? 'all' : String(sid)}
              onValueChange={(v) => {
                const nextFilters = { ...filters };
                if (v === 'all') delete nextFilters.sid;
                else nextFilters.sid = Number(v);
                onChange({ ...widget, filters: nextFilters });
              }}
              className="w-32"
            >
              <SelectItem value="all">{et.allStores}</SelectItem>
              {stores.map((s) => (
                <SelectItem key={s.sid} value={String(s.sid)}>{s.name}</SelectItem>
              ))}
            </Select>
          </span>
        )}

        {widget.kind === 'report' && (
          <span className="flex items-center gap-1">
            <span className="text-tremor-content">{et.from}</span>
            <input
              type="date"
              value={typeof filters.from === 'string' ? filters.from : ''}
              onChange={(e) => {
                const nextFilters = { ...filters };
                if (e.target.value) nextFilters.from = e.target.value;
                else delete nextFilters.from;
                onChange({ ...widget, filters: nextFilters });
              }}
              className="rounded-tremor-small border border-tremor-border bg-tremor-background px-1 py-0.5 text-xs"
            />
            <span className="text-tremor-content">{et.to}</span>
            <input
              type="date"
              value={typeof filters.to === 'string' ? filters.to : ''}
              onChange={(e) => {
                const nextFilters = { ...filters };
                if (e.target.value) nextFilters.to = e.target.value;
                else delete nextFilters.to;
                onChange({ ...widget, filters: nextFilters });
              }}
              className="rounded-tremor-small border border-tremor-border bg-tremor-background px-1 py-0.5 text-xs"
            />
          </span>
        )}

        <button
          type="button"
          onClick={onRemove}
          className="ml-auto hover:text-red-600 dark:hover:text-red-400"
          aria-label={et.remove}
        >✕ {et.remove}</button>
      </div>
      {children}
    </div>
  );
}
